// XCentral Hub — THE route registry (Story S1 #16040, Epic #16035). Replaces LoopHubTabs.tsx.
//
// WHY THIS FILE IS THE POINT OF THE STORY. The hub's six horizontal tabs become the app's own
// SIDEBAR (thirteen entries under the "XCentral Hub" area), and every one of the thirteen pages is
// registered HERE, up front — including the ones nobody has built yet. Each unbuilt page already
// has its own component file rendering the #12311 In-Development placeholder, so a later Story
// (S11 #16050 Build Plan, S13 #16052 Blockages, S14 #16053 Operators, S15 #16054 Claim History, …)
// replaces the CONTENTS of its own file and touches nothing shared. Without that, every page Story
// would edit this file plus the nav plus the router, and the epic would serialise — the defect
// measured fleet-wide on 2026-08-13, where 92 of 115 items were tagged to serialise.
//
// ONE array, three consumers, so they cannot drift:
//   1. `layouts/navRegistry.ts`   — the sidebar items for the XCentral Hub area (BR-NAV-006).
//   2. `LoopHubCockpit.tsx`       — which panel a `:section` renders, and the page title.
//   3. `App.tsx`                  — the routes, plus the legacy `/loop-hub/*` redirects.
// A route missing from this array is a route a later Story has to add to the shared nav file, which
// is exactly the serialisation this registry exists to prevent. `XCENTRAL_HUB_ROUTES.length === 13`
// is asserted by test for that reason, not for tidiness. (Now 14 — S21 #16602 added Job Queues.)
//
// PURE DATA — no React, no imports. `navRegistry.ts` (a layout module) reads it, so keeping it free
// of components keeps that import one-directional and cycle-free.
//
// LABEL vs API PREFIX. The UI label reads "XCentral Hub" and the UI route is `/xcentral-hub`; the
// `api/v1/loop-hub/*` REST prefix is deliberately UNTOUCHED (AC3) — renaming it would break all
// seven fleet machines at once and needs a coordinated rollout. The source directory and the
// `LoopHub*` component names also stay as they are: every sibling Story in this Epic names files
// under `pages/loop-hub/`, so moving them would break the plans this Story exists to unblock.
//
// Source of truth for the "what": the signed-off prototype
// `docs/specs/tsp-xcentral/working/mockups/mockup-system-prototype.html` (nav registry ~line 7768).
// Order below is prototype order and is load-bearing — AC1.

/** The UI base for every hub page. NOT the API prefix (`api/v1/loop-hub/*`, unchanged — AC3). */
export const XCENTRAL_HUB_BASE = '/xcentral-hub';

/** The pre-#16040 UI base, kept only so old deep links redirect rather than 404 (AC6). */
export const LEGACY_HUB_BASE = '/loop-hub';

/** The area label. Exported so the nav, the breadcrumbs and the tests all read one string. */
export const XCENTRAL_HUB_LABEL = 'XCentral Hub';

export type HubRouteKey =
  | 'hub-home'
  | 'hub-portfolio'
  | 'hub-projects'
  | 'hub-project'
  | 'hub-buildplan'
  | 'hub-queues'
  // 'hub-approvals' RETIRED by #16927 — the Approvals page was `GateQueuePanel` +
  // `PipelineApprovalsPanel` stacked, and both are now tabs on the Hub Inbox. The key is removed
  // rather than kept-and-unused so `PANELS` (a Record over this union) fails to compile if anyone
  // reintroduces a route without a component, or leaves a component with no route.
  // `approvals` survives as an alias on `hub-inbox` below.
  | 'hub-inbox'
  // Story #22044 — Hub sessions: every Claude Code session on every machine.
  | 'hub-sessions'
  | 'hub-plangate'
  | 'hub-machines'
  | 'hub-operators'
  // Story #21327 — Seats & Subscriptions (#p-hub-seats).
  | 'hub-seats'
  | 'hub-claims'
  | 'hub-candidates'
  | 'hub-agents'
  | 'hub-client-platforms'
  | 'hub-ideas-triage';

/**
 * Story #26091 — the hub's two role sets, in ONE place. The API mirrors them: `ViewPolicy` admits
 * {@link HUB_READ_ROLES}; every write names a policy narrower than it (`WritePolicy`, `ActPolicy`, …).
 *
 * - {@link HUB_ACT_ROLES} — the human operators: Owner, Admin and App (App has Admin parity, Story #26090).
 * - {@link HUB_READ_ROLES} — those plus Operations and Infra, who READ the hub and act on nothing through it
 *   (BR-HUB-A-944, BR-HUB-A-945, BR-HUB-A-943).
 *
 * Hiding a control is UX; the API is the security boundary (Constitution Article IV) and answers 403 regardless.
 */
export const HUB_ACT_ROLES: readonly string[] = ['Owner', 'Admin', 'App'];
export const HUB_READ_ROLES: readonly string[] = [...HUB_ACT_ROLES, 'Operations', 'Infra'];

export interface XCentralHubRoute {
  /** The prototype's panel id — the stable key a later Story cites. */
  readonly key: HubRouteKey;
  /**
   * Story #26091 — who may see this page: its sidebar entry AND its route guard read this, so the two cannot
   * drift. {@link HUB_READ_ROLES} for the pages the prototype grants Operations and Infra; {@link HUB_ACT_ROLES}
   * for the rest.
   */
  readonly roles: readonly string[];
  /** URL segment under `XCENTRAL_HUB_BASE`. `''` is the index page (`/xcentral-hub`). */
  readonly slug: string;
  readonly label: string;
  readonly icon: string;
  /**
   * Rendered as a trailing chip in the sidebar. `not mocked` marks a page the approved IA lists
   * but the prototype deliberately does not draw (AC5) — it is not "coming soon", it is "no design
   * yet", and conflating the two is what makes a chipped entry read as a promise.
   */
  readonly chip?: string;
  /**
   * Slugs that still resolve to this route after it was renamed — Story #16856.
   *
   * <b>An alias is a promise about someone else's bookmark.</b> A hub page lives in a browser
   * favourite, a chat message and a runbook, none of which this repo can rewrite; renaming the slug
   * without an alias turns every one of them into a blank page, and the operator's first symptom is
   * "the hub is broken", not "the page moved". The alias costs one array lookup and removes that
   * entirely.
   *
   * It is deliberately NOT rendered in the nav and NOT canonical: {@link hubPath} always builds the
   * new slug, so links created from here converge on the new name while old ones keep working.
   */
  readonly aliases?: readonly string[];
}

/**
 * All SEVENTEEN hub pages, in prototype order (AC1). Every one is routable (AC4) and none is a dead
 * link. Grilled 2026-08-31: the earlier "nine hub pages" was wrong; the prototype listed thirteen.
 * Job Queues became the fourteenth with S21 #16602, Client Platforms the fifteenth with S24 #16606,
 * and Ideas Triage the sixteenth with S25 #16607. (Approvals left with #16927 and Sessions joined
 * with #22044.) Seats & Subscriptions is the seventeenth with Story #21327.
 */
export const XCENTRAL_HUB_ROUTES: readonly XCentralHubRoute[] = [
  { key: 'hub-home', slug: '', label: 'Overview', icon: '🧭', roles: HUB_READ_ROLES },
  { key: 'hub-portfolio', slug: 'portfolio', label: 'Portfolio', icon: '📊', roles: HUB_ACT_ROLES },
  { key: 'hub-projects', slug: 'projects', label: 'Hub projects', icon: '🗂️', roles: HUB_READ_ROLES },
  { key: 'hub-project', slug: 'project', label: 'Project detail', icon: '📁', roles: HUB_READ_ROLES },
  { key: 'hub-buildplan', slug: 'build-plan', label: 'Build Plan', icon: '🧱', roles: HUB_ACT_ROLES },
  // BUILT by S21 #16602. Placed HERE, before Approvals, because the prototype's nav registry
  // (~line 9355) lists `hub-queues` between Hub Projects and Approvals and the order is load-bearing
  // (AC1) — this is the first position in the shipped array that preserves that relative order.
  { key: 'hub-queues', slug: 'queues', label: 'Job Queues', icon: '📋', roles: HUB_ACT_ROLES },
  // APPROVALS LEFT THE SIDEBAR in #16927. Two doors to one room was the problem: "what needs me?"
  // was answered on this page AND on the Hub Inbox, and this one was only ever `GateQueuePanel` +
  // `PipelineApprovalsPanel` stacked. Both are now tabs on the Hub Inbox, so the entry is gone from
  // the nav and `approvals` is an alias on `hub-inbox` — landing on the PR Approvals tab.
  //
  // RENAMED by S #16856 — "Machine Blockages" became the HUB INBOX when the queue stopped being
  // only about stuck machines. It now holds three kinds of item: a machine that is stuck, a grill
  // PLAN awaiting acceptance, and an EPIC REVIEW. The old name described one of the three and would
  // have made the other two look like they were filed in the wrong place.
  //
  // `blockages` and `approvals` both stay aliases so existing bookmarks, chat links and runbooks
  // keep resolving. Two aliases on one route is the expected shape now, not an anomaly: this page
  // has absorbed two previously-separate destinations.
  { key: 'hub-inbox', slug: 'inbox', label: 'Hub Inbox', icon: '📥', aliases: ['blockages', 'approvals'], roles: HUB_READ_ROLES },
  // BUILT by Story #22044 (Feature #22043, module hub-sessions). Placed HERE because the prototype's tab row draws
  // Sessions between Hub Inbox and Build Machines (#p-hub-sessions), and this is the first position in the shipped
  // array that keeps that relative order.
  { key: 'hub-sessions', slug: 'sessions', label: 'Sessions', icon: '💬', roles: HUB_ACT_ROLES },
  { key: 'hub-plangate', slug: 'plan-gate', label: 'Plan Gate', icon: '🚦', roles: HUB_ACT_ROLES },
  { key: 'hub-machines', slug: 'machines', label: 'Build Machines', icon: '🖥️', roles: HUB_READ_ROLES },
  { key: 'hub-operators', slug: 'operators', label: 'Operators', icon: '👤', roles: HUB_READ_ROLES },
  // BUILT by Story #21327 (Feature #21322). Placed directly AFTER Operators because that is where the
  // prototype's nav order draws Seats & Subscriptions; this keeps the relative order load-bearing (AC1).
  { key: 'hub-seats', slug: 'seats', label: 'Seats & Subscriptions', icon: '🪪', roles: HUB_ACT_ROLES },
  // BUILT by S15 #16054 — the `not mocked` chip retires with it. The prototype still captures a
  // per-machine, last-24h view inside Build Machines and that block is unchanged; this page answers
  // the different question the Story's acceptance criteria ask (one project, filterable, over the
  // retention window), which is why it stayed a page of its own rather than folding in.
  { key: 'hub-claims', slug: 'claims', label: 'Claim History', icon: '📜', roles: HUB_ACT_ROLES },
  { key: 'hub-candidates', slug: 'candidates', label: 'Candidate projects', icon: '🌱', roles: HUB_ACT_ROLES },
  { key: 'hub-agents', slug: 'agents', label: 'Pipeline agents', icon: '🤖', roles: HUB_READ_ROLES },
  // BUILT by S24 #16606 (Feature #16604). APPENDED, deliberately: this is a net-new page with no
  // position in the prototype's nav registry, and appending is the only placement that cannot
  // disturb the relative order of the fourteen entries above — which IS load-bearing (AC1 of
  // #16040). If the operator later wants it elsewhere, moving one line is the whole change.
  { key: 'hub-client-platforms', slug: 'client-platforms', label: 'Client Platforms', icon: '📡', roles: HUB_ACT_ROLES },
  // BUILT by S25 #16607 (Feature #16604). APPENDED for the same reason S24 was: it is net-new, has
  // no position in the prototype's nav registry, and appending is the only placement that cannot
  // disturb the load-bearing relative order of the entries above.
  { key: 'hub-ideas-triage', slug: 'ideas-triage', label: 'Ideas Triage', icon: '💡', roles: HUB_ACT_ROLES },
];

/** The full path for a hub route — `/xcentral-hub` for the index, `/xcentral-hub/<slug>` otherwise. */
export function hubPath(slug: string): string {
  return slug === '' ? XCENTRAL_HUB_BASE : `${XCENTRAL_HUB_BASE}/${slug}`;
}

/** The registry entry for a `:section` param, or `undefined` for an unknown segment. */
export function hubRouteBySlug(slug: string | undefined): XCentralHubRoute | undefined {
  const wanted = slug ?? '';
  // Canonical slug first, ALWAYS. An alias must never be able to shadow a live page's real slug —
  // if a future route legitimately takes a name that is some older route's alias, the live page
  // wins and the stale bookmark is the thing that breaks, which is the right way round.
  return (
    XCENTRAL_HUB_ROUTES.find(r => r.slug === wanted) ??
    XCENTRAL_HUB_ROUTES.find(r => r.aliases?.includes(wanted))
  );
}

/** Story #26091 — true when a caller holding `roles` may see `route` (its nav entry and its page). */
export function canSeeHubRoute(route: XCentralHubRoute, roles: readonly string[]): boolean {
  return route.roles.some(role => roles.includes(role));
}

/** Story #26091 — true when `roles` reach the hub at all (they read at least Overview). */
export function canReadHub(roles: readonly string[]): boolean {
  return HUB_READ_ROLES.some(role => roles.includes(role));
}

/** Story #26091 — true when `roles` may act through the hub (the write controls render). */
export function canActInHub(roles: readonly string[]): boolean {
  return HUB_ACT_ROLES.some(role => roles.includes(role));
}

/**
 * Where an old `/loop-hub/<segment>` deep link goes (AC6). Every pre-#16040 tab slug survives the
 * move unchanged, so the mapping is "same segment under the new base" — but it is resolved through
 * the registry rather than string-concatenated, so a segment that is NOT a hub page (a typo, or a
 * tab that no longer exists) lands on Overview instead of a blank shell.
 */
export function legacyHubRedirect(segment: string | undefined): string {
  return hubPath(hubRouteBySlug(segment)?.slug ?? '');
}
